import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faXmark,
  faArrowUpRightFromSquare,
} from "@fortawesome/free-solid-svg-icons";

interface TeamCardModalProps {
  fullName: string;
  role: string;
  investsIn: string;
  bio: string;
  firmWebsite: string;
  thesis: string;
  averageCheckSize: string;
  valueProp: string;
  investorDirectoryUrl: string;
  numberOfInvestments: string;
  onClose: () => void;
  imageSrc: string;
}

const TeamCardModal: React.FC<TeamCardModalProps> = ({
  fullName,
  role,
  investsIn,
  bio,
  firmWebsite,
  thesis,
  averageCheckSize,
  valueProp,
  investorDirectoryUrl,
  numberOfInvestments,
  onClose,
  imageSrc,
}) => {
  // "Common" parts of the modal content that appear in both mobile & desktop
  // (We’ll embed them below, so that we don’t duplicate all the logic.)
  const investsInContent = (
    <div className="flex flex-wrap gap-2">
      {investsIn.split(",").map((item, i) => (
        <div key={i} className="px-2.5 py-1 rounded border border-[#e3e3de]">
          <div className="text-[#122427] text-sm font-medium font-['Manrope']">
            {item.trim()}
          </div>
        </div>
      ))}
    </div>
  );

  const statsContent = (
    <>
      {/* Average Check Size */}
      <div className="flex flex-col gap-1">
        <div className="text-[#717c79] text-sm font-medium font-['Manrope']">
          Average check size
        </div>
        <div className="text-[#122427] text-base font-medium font-['Manrope']">
          {averageCheckSize}
        </div>
      </div>

      {/* Number of Investments */}
      <div className="flex flex-col gap-1">
        <div className="text-[#717c79] text-sm font-medium font-['Manrope']">
          # of investments in the last 12 months
        </div>
        <div className="text-[#122427] text-base font-medium font-['Manrope']">
          {numberOfInvestments}
        </div>
      </div>
    </>
  );

  const bioThesisValue = (
    <>
      {/* Bio */}
      <div className="flex flex-col gap-2">
        <div className="text-[#717c79] text-sm font-medium font-['Manrope']">
          Bio
        </div>
        <div className="text-[#36403f] text-sm font-medium font-['Manrope']">
          {bio}
        </div>
      </div>

      {/* Investing thesis */}
      <div className="flex flex-col gap-2">
        <div className="text-[#717c79] text-sm font-medium font-['Manrope']">
          Investing thesis
        </div>
        <div className="text-[#36403f] text-sm font-medium font-['Manrope']">
          {thesis}
        </div>
      </div>

      {/* Value prop */}
      <div className="flex flex-col gap-2">
        <div className="text-[#717c79] text-sm font-medium font-['Manrope']">
          Value prop
        </div>
        <div className="text-[#36403f] text-sm font-medium font-['Manrope']">
          {valueProp}
        </div>
      </div>
    </>
  );

  const linksContent = (
    <>
      {/* Firm Website Link */}
      {firmWebsite && (
        <a
          href={firmWebsite}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-[#132527] text-sm font-semibold font-['Manrope']"
        >
          {firmWebsite}
          <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
        </a>
      )}

      {/* Investor Directory Link */}
      {investorDirectoryUrl && (
        <a
          href={investorDirectoryUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-[#132527] text-sm font-semibold font-['Manrope']"
        >
          Visit Investor Directory
          <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
        </a>
      )}
    </>
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onClick={onClose}
    >
      {/* Stop click from closing modal */}
      <div
        onClick={(e) => e.stopPropagation()}
        className="
          relative
          w-full
          max-w-[440px]
          md:max-w-[720px]
          max-h-[90vh]
          bg-[#fcfbf8]
          flex
          flex-col
          overflow-y-auto
        "
      >
        {/* Close button (top-right) - same for both mobile & desktop */}
        <button
          className="absolute top-4 right-4 text-[#36403f] text-2xl z-10"
          onClick={onClose}
        >
          <FontAwesomeIcon icon={faXmark} />
        </button>

        {/*
          ==========================
          MOBILE LAYOUT (V1)
          Hidden on md+
          ==========================
        */}
        <div className="block md:hidden">
          {/* Header / Image */}
          <div className="relative">
            <div className="p-6 bg-[#f2f1eb]">
              {imageSrc ? (
                <img
                  src={imageSrc}
                  alt={fullName}
                  className="w-full aspect-square object-cover rounded"
                />
              ) : (
                <div className="w-full aspect-square bg-gray-200 rounded" />
              )}
            </div>
            {/* Close button is already placed absolutely above */}
          </div>

          {/* Body */}
          <div className="p-6 flex flex-col gap-10">
            {/* Name / Role */}
            <div className="flex flex-col gap-1">
              <div className="text-[#122427] text-[28px] font-semibold leading-10 font-['Manrope']">
                {fullName}
              </div>
              <div className="text-[#717c79] text-base font-medium leading-7 font-['Manrope']">
                {role}
              </div>
            </div>

            {/* Stats */}
            <div className="flex flex-col gap-4">
              {/* Invests In */}
              <div className="flex flex-col gap-2">
                <div className="text-[#717c79] text-sm font-medium font-['Manrope']">
                  Invests in
                </div>
                {investsInContent}
              </div>

              {statsContent}
            </div>

            {bioThesisValue}

            {linksContent}
          </div>
        </div>

        {/*
          ==========================
          DESKTOP LAYOUT (V2)
          Hidden below md
          ==========================
        */}
        <div className="hidden md:block">
          {/* TOP SECTION */}
          <div className="w-full h-auto p-8 bg-[#ebebe5] flex flex-col justify-start items-start gap-8">
            {/*
              Row container that holds:
              - Left side: 'Top SMB Investor' tag & Name/Role
              - Right side: Photo
            */}
            <div className="w-full flex justify-start items-start gap-8">
              {/* LEFT SIDE */}
              <div className="w-[424px] min-h-[240px] py-1 flex flex-col justify-between items-start">
                {/* Tag */}
                <div>
                  <div className="px-2.5 py-0.5 bg-[#cd6944] rounded inline-flex">
                    <div className="text-[#fcfbf8] text-sm font-semibold font-['Manrope'] leading-normal tracking-tight">
                      Top SMB Investor
                    </div>
                  </div>
                </div>

                {/* Name and Role */}
                <div className="flex flex-col justify-start items-start gap-2">
                  <div className="text-[#122427] text-[32px] font-semibold font-['Manrope'] leading-[48px]">
                    {fullName}
                  </div>
                  <div className="text-[#717c79] text-base font-medium font-['Manrope'] leading-7 tracking-tight">
                    {role}
                  </div>
                </div>
              </div>

              {/* RIGHT SIDE: PHOTO (240 x 240) */}
              <img
                src={imageSrc}
                alt={fullName}
                className="w-[240px] h-[240px] p-3 rounded-sm object-cover"
              />
            </div>
          </div>

          {/* BOTTOM SECTION */}
          <div className="w-full h-auto p-8 flex flex-col justify-start items-start gap-8">
            {/* INVESTS IN */}
            <div className="flex flex-col justify-start items-start gap-3">
              <div className="text-[#717c79] text-sm font-medium font-['Manrope'] leading-normal tracking-tight">
                Invests in
              </div>
              {investsInContent}
            </div>

            {/* Average check size & # of investments */}
            <div className="w-full flex flex-col md:flex-row justify-start items-start gap-4">
              {statsContent}
            </div>

            {bioThesisValue}

            {linksContent}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamCardModal;
